/**
 * ORION — Visual Domain
 *
 * File: CanvasBoard.tsx
 *
 * Responsibility:
 * - Macro-view of the Cognitive System.
 * - Lists and organizes Cognitive Objects.
 * - Entry point for selecting objects for inspection.
 *
 * What this file does NOT do:
 * - Does not implement detailed inspection logic (delegates to ObjectInspector).
 * - Does not contain business logic for object creation (delegates to Services).
 *
 * Depends on:
 * - cognitionService (Data)
 * - ObjectInspector (Sub-component)
 */
import React, { useEffect, useState } from 'react';
import { cognitionService } from '../cognition/cognitionService';
import type { CognitiveObject } from '../cognition/cognitionService';
import { ObjectInspector } from './ObjectInspector';

export const CanvasBoard: React.FC = () => {
    const [objects, setObjects] = useState<CognitiveObject[]>([]);
    const [selectedObjectId, setSelectedObjectId] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        loadObjects();
    }, []);

    const loadObjects = async () => {
        try {
            setLoading(true);
            const data = await cognitionService.listObjects();
            setObjects(data);
            setError(null);
        } catch (err: any) {
            setError(err.message || 'Failed to list objects');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex h-screen bg-gray-900 text-white overflow-hidden">
            {/* Left Panel: Object List */}
            <div className="w-1/4 min-w-[300px] border-r border-gray-700 flex flex-col">
                <div className="p-4 border-b border-gray-700 bg-gray-800">
                    <h2 className="text-xl font-bold font-mono text-cyan-400">ORION CANVAS</h2>
                    <div className="text-xs text-gray-500 mt-1 uppercase tracking-wider">System Observer</div>
                </div>

                <div className="flex-1 overflow-y-auto p-2 space-y-2">
                    {loading && <div className="text-gray-500 p-4 text-center">Loading objects...</div>}
                    {error && <div className="text-red-400 p-4 text-center text-sm">{error}</div>}

                    {!loading && objects.map(obj => (
                        <div
                            key={obj.id}
                            onClick={() => setSelectedObjectId(obj.id)}
                            className={`
                                    p-3 rounded cursor-pointer transition-all border
                                    ${selectedObjectId === obj.id
                                    ? 'bg-cyan-900/30 border-cyan-500/50 shadow-[0_0_10px_rgba(6,182,212,0.2)]'
                                    : 'bg-gray-800 border-transparent hover:bg-gray-750 hover:border-gray-600'}
                                `}
                        >
                            <div className="flex justify-between items-start mb-1">
                                <span className="font-mono text-xs text-gray-400 truncate w-2/3" title={obj.id}>
                                    {obj.id.substring(0, 8)}...
                                </span>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded ${getStatusColor(obj.current_state)}`}>
                                    {obj.current_state}
                                </span>
                            </div>
                            <div className="text-sm font-medium text-gray-200">
                                {obj.metadata?.title || obj.object_type || 'Untitled Object'}
                            </div>
                            <div className="text-xs text-gray-500 mt-1 flex justify-between">
                                <span>v{obj.version}</span>
                                <span>{new Date(obj.updated_at).toLocaleDateString()}</span>
                            </div>
                        </div>
                    ))}

                    {!loading && objects.length === 0 && (
                        <div className="text-gray-600 p-8 text-center italic">No cognitive objects found.</div>
                    )}
                </div>

                <div className="p-3 border-t border-gray-700 bg-gray-800 text-xs text-center text-gray-500">
                    {objects.length} Objects Loaded
                </div>
            </div>

            {/* Right Panel: Inspector Area */}
            <div className="flex-1 flex flex-col bg-slate-950 relative">
                {selectedObjectId ? (
                    // <ObjectInspector> is rendered here
                    <ObjectInspector object={objects.find(o => o.id === selectedObjectId)!} />
                ) : (
                    <div className="flex items-center justify-center h-full text-gray-700">
                        <p className="font-light tracking-wide">Select an object to inspect</p>
                    </div>
                )}
            </div>
        </div>
    );
};

function getStatusColor(status: string): string {
    switch (status) {
        case 'DECIDED': return 'bg-green-900 text-green-300';
        case 'FINALIST': return 'bg-emerald-900 text-emerald-300';
        case 'STRESS_TEST': return 'bg-orange-900 text-orange-300';
        case 'POLLINATION': return 'bg-blue-900 text-blue-300';
        case 'CREATED': return 'bg-gray-700 text-gray-300';
        case 'PAUSED': return 'bg-yellow-900 text-yellow-300';
        case 'AWAITING_HUMAN': return 'bg-red-900 text-red-300 animate-pulse';
        default: return 'bg-gray-800 text-gray-400';
    }
}
